import { InjectionToken, inject } from '@angular/core'

import { signalStore, withProps } from '@ngrx/signals'
import { Store } from '@ngrx/store'

import {
  ACCESS_MANAGEMENT_GROUPS_API,
  ACCESS_MANAGEMENT_ROLES_API,
} from '@flowx/designer/modules/workspaces/shared/workspaces'
import { OrganizationAdminDesignerUsersApiService } from '@flowx/designer/shared/api/organization-admin-designer-users.api'
import { OrganizationAdminRuntimeUsersApiService } from '@flowx/designer/shared/api/organization-admin-runtime-users.api'
import { SelfEditUserApiService } from '@flowx/designer/shared/api/self-edit-user.api'
import { WorkspaceUsersApiService } from '@flowx/designer/shared/api/workspace-users.api'
import { ApplicationsService } from '@flowx/designer/shared/services/applications.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

import { withGroupsFeature } from './groups/groups.store'
import { withRolesFeature } from './roles/roles.store'
import { withOrgAdminUsersFeature } from './users/users-org-admin.store'
import { withWorkspaceUsersFeature } from './users/users-workspace.store'

/**
 * Two scope-specific access-management stores. They share the (scope-agnostic) groups and roles
 * features and differ only in their users feature, which injects the concrete API(s) of its
 * scope — removing the user-type resolver and the `as` casts the previous single store needed.
 *
 * Order matters: the users feature must come first because the groups feature updates its
 * `userGroupsResource`.
 */
export const WorkspaceAccessManagementStore = signalStore(
  withProps(() => ({
    _applicationsService: inject(ApplicationsService),
    _toastMessageService: inject(ToastMessageService),
    _groupsApi: inject(ACCESS_MANAGEMENT_GROUPS_API),
    _rolesApi: inject(ACCESS_MANAGEMENT_ROLES_API),
    _ngrxStore: inject(Store),
    _usersApi: inject(WorkspaceUsersApiService),
  })),
  withWorkspaceUsersFeature(),
  withGroupsFeature(),
  withRolesFeature()
)

export const OrgAdminAccessManagementStore = signalStore(
  withProps(() => ({
    _applicationsService: inject(ApplicationsService),
    _toastMessageService: inject(ToastMessageService),
    _groupsApi: inject(ACCESS_MANAGEMENT_GROUPS_API),
    _rolesApi: inject(ACCESS_MANAGEMENT_ROLES_API),
    _ngrxStore: inject(Store),
    _designerUsersApi: inject(OrganizationAdminDesignerUsersApiService),
    _runtimeUsersApi: inject(OrganizationAdminRuntimeUsersApiService),
    _selfEditUserApi: inject(SelfEditUserApiService),
  })),
  withOrgAdminUsersFeature(),
  withGroupsFeature(),
  withRolesFeature()
)

/**
 * Surface shared by both stores — the groups/roles features plus the scope-agnostic user reads.
 * Scope-divergent user operations (invite, bulk-invite, admin-role, per-type delete/update,
 * workspace asset removal, etc.) are intentionally excluded: components that need them inject
 * the concrete `WorkspaceAccessManagementStore` / `OrgAdminAccessManagementStore` directly.
 */
export type AccessManagementStoreContract = Pick<
  InstanceType<typeof OrgAdminAccessManagementStore>,
  // groups feature
  | 'groupsLoading'
  | 'groupsGridSearch'
  | 'selectedGroupId'
  | 'getGroupAssetSummaries'
  | 'getGroupMemberCandidates'
  | 'getGroupSolutionAccessAssets'
  | 'getGroupAssetCandidates'
  | 'selectedGroupResource'
  | 'groupSolutionAccessAssets'
  | 'groupUsersGridSearch'
  | 'selectedGroup'
  | 'groupSolutionAccessAssetsTotalElements'
  | 'createGroup'
  | 'updateGroup'
  | 'deleteGroup'
  | 'addUsersToGroup'
  | 'removeUserFromGroup'
  // roles feature
  | 'availableRoles'
  | 'loadRoles'
  | 'addRole'
  | 'updateRoleBasicInfo'
  | 'getRoleById'
  | 'getRolePermissions'
  | 'updateRolePermissions'
  | 'getRolesByAssetType'
  // scope-agnostic user reads
  | 'selectedUser'
  | 'selectedUserId'
  | 'userGroups'
  | 'usersLoading'
  | 'usersGridSearch'
  | 'getUserSummaries'
  | 'getUserForEditing'
  | 'createUser'
  | 'updatePassword'
  | 'searchUsers'
  | 'refreshUsersGrid'
  | 'resendInvite'
>

/**
 * Injection token for the scope-agnostic surface. Each scope owner provides its concrete store
 * under this token (`useExisting`), so groups/roles/common-user consumers inject this without
 * caring which scope they render in.
 */
export const AccessManagementStore = new InjectionToken<AccessManagementStoreContract>(
  'AccessManagementStore'
)
